import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FileDown, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const eur = (v) =>
  Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const MONTHS_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

const CAT_ICONS = { Reise: "✈", Büro: "📎", Software: "💻", Bewirtung: "🍽", Fahrt: "🚗", Sonstiges: "🧾" };

function monthLabel(ym) {
  const [y, m] = ym.split("-");
  return `${MONTHS_DE[Number(m) - 1] || ""} ${y}`;
}

export default function ReceiptsSharePage() {
  const { token } = useParams();
  return <ShareInner token={token} />;
}

function ShareInner({ token }) {
  const [state, setState] = useState("loading");
  const [receipts, setReceipts] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/receipts/share/${encodeURIComponent(token)}`);
        if (res.status === 403) throw new Error("Dieser Link ist ungültig.");
        if (!res.ok) throw new Error("Belege konnten nicht geladen werden.");
        const data = await res.json();
        if (!on) return;
        setReceipts(data.receipts || []);
        setState("ok");
      } catch (err) {
        if (on) {
          setState("error");
          setError(err.message);
        }
      }
    })();
    return () => {
      on = false;
    };
  }, [token]);

  const groups = useMemo(() => {
    const g = {};
    receipts.forEach((r) => {
      const ym = (r.date || "").slice(0, 7) || "ohne Datum";
      (g[ym] = g[ym] || []).push(r);
    });
    return Object.keys(g).sort().reverse().map((ym) => ({ ym, items: g[ym], sum: g[ym].reduce((s, r) => s + Number(r.amount || 0), 0) }));
  }, [receipts]);

  return (
    <div className="min-h-screen bg-dark text-bone">
      <div className="grain" aria-hidden="true" />
      <div className="mx-auto max-w-4xl px-5 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Joudi Ltd · Belege</p>
            <h1 className="mt-2 font-display text-3xl font-medium tracking-tight">Quittungen &amp; Belege</h1>
            <p className="mt-2 text-sm text-bone/60">
              Geschützte Belegübersicht für die Buchhaltung — zum Ansehen einfach klicken, als Datei über den Download-Pfeil.
            </p>
          </div>
          <div className="rounded-[3px] border border-gold/30 bg-gold/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
            {receipts.length} Belege
          </div>
        </div>

        {state === "loading" && <p className="mt-16 font-mono text-xs uppercase tracking-[0.3em] text-bone/40">Lade Belege …</p>}

        {state === "error" && (
          <div className="mt-16 flex items-center gap-3 rounded-[3px] border border-red-500/40 bg-red-500/10 px-5 py-4 text-sm text-red-300">
            <ShieldAlert size={18} /> <span>{error || "Dieser Link ist ungültig."}</span>
          </div>
        )}

        {state === "ok" && groups.length === 0 && (
          <p className="mt-16 text-sm text-bone/50">Noch keine Belege vorhanden.</p>
        )}

        {state === "ok" && (
          <div className="mt-10 space-y-10" data-testid="share-list">
            {groups.map((g) => (
              <div key={g.ym}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-bone/10 pb-2">
                  <h2 className="font-display text-lg text-bone">{monthLabel(g.ym)}</h2>
                  <p className="font-mono text-xs text-bone/60">
                    {g.items.length} Beleg(e) · Summe <span className="text-gold">{eur(g.sum)}</span>
                  </p>
                </div>
                <div className="mt-4 space-y-3">
                  {g.items.map((r, i) => (
                    <div key={r.id} data-testid={`share-row-${g.ym}-${i + 1}`} className="flex items-center gap-4 rounded-[3px] border border-bone/10 bg-carddark/60 p-3">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[3px] border border-bone/10 bg-dark">
                        {r.content_type && r.content_type.startsWith("image/") ? (
                          <img src={`${API}/api/receipts/share/${encodeURIComponent(token)}/file/${r.id}`} alt={r.merchant || "Beleg"} className="h-full w-full object-cover" />
                        ) : (
                          <FileDown size={18} className="text-gold" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{r.merchant || r.filename || "Beleg"}</p>
                        <p className="truncate text-xs text-bone/50">
                          {r.date} {r.category ? `· ${r.category}` : ""}{r.note ? ` · ${r.note}` : ""}
                        </p>
                      </div>
                      <p className="shrink-0 font-mono text-sm text-bone">{eur(r.amount)}</p>
                      <a
                        data-testid={`share-download-${g.ym}-${i + 1}`}
                        href={`${API}/api/receipts/share/${encodeURIComponent(token)}/file/${r.id}`}
                        download={r.filename || "beleg"}
                        className="inline-flex shrink-0 items-center gap-2 rounded-[3px] border border-bone/15 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-bone/70 transition-colors hover:border-gold hover:text-gold"
                      >
                        <FileDown size={13} /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-14 border-t border-bone/10 pt-6 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-bone/30">
          Joudi Ltd · Tassou Markou 1 · 8560 Peyia · Zypern · USt-IdNr. HE499162
        </p>
      </div>
    </div>
  );
}
